import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

import { authenticateUser } from 'src/auth';
import FlashesRenderer from 'src/components/Flashes/FlashesRenderer';
import LayoutErrorBoundary from 'src/components/Layout/LayoutErrorBoundary';
import Loading from 'src/components/Loading';
import CustomerNavigation from 'src/components/Navigation/CustomerNavigation';
import Footer from 'src/enosikit/components/Footer';
import { withTracingHOC } from 'src/tracing';

/**
 * Renders the authenticated page shell for a customer view, including the navigation bar,
 * flash messages, page content, and footer. Returns a loading spinner while authentication
 * is being resolved.
 * @param {object} props
 * @param {React.ReactNode} props.children - Page content to render inside the layout.
 * @param {object} props.router - React Router object used by authenticateUser for redirects.
 * @param {object|null} props.viewer - Relay fragment data for the authenticated viewer.
 * @returns {React.ReactComponentElement} The composed layout or a loading spinner.
 */
function CustomerLayoutContent({
  children, router, viewer = null,
}) {
  const intl = useIntl();

  if (!authenticateUser(intl, viewer, null, router)) {
    return <Loading />;
  }

  const pageTitle = intl.formatMessage({ id: 'layout.customer_layout.page.title', defaultMessage: 'Enosi' });
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <div className="flex flex-column" style={{ minHeight: '80vh' }}>
        <CustomerNavigation viewer={viewer} />
        <section id="main">
          <FlashesRenderer />
          <div className="container">
            <div className="flex-auto">{children}</div>
          </div>
        </section>
      </div>

      <Footer versionUI={VERSION} />
    </>
  );
}

const customerLayoutContentPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string,
    viewerUser: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }),
};

CustomerLayoutContent.propTypes = customerLayoutContentPropTypes;

// Layout fragment is deliberately non-throwing. Rationale:
// docs/solutions/graphql/customer-navigation-field-error-handling.md
const CustomerLayoutFragmentContainer = createFragmentContainer(CustomerLayoutContent, {
  viewer: graphql`
    fragment CustomerLayout_viewer on Viewer {
      id
      viewerUser {
        type
      }
      ...CustomerNavigation_viewer
    }
  `,
});

const TracedCustomerLayout = withTracingHOC('CustomerLayout', CustomerLayoutFragmentContainer);

/**
 * CustomerLayout component that provides an error boundary around the customer page shell.
 * @param {object} props
 * @param {React.ReactNode} props.children - Page content to render inside the layout.
 * @param {object} props.router - React Router object used for redirects.
 * @param {object|null} props.viewer - Relay fragment data for the authenticated viewer.
 * @returns {React.ReactComponentElement} - CustomerLayout component.
 */
function CustomerLayout({
  children, router, viewer = null,
}) {
  return (
    <LayoutErrorBoundary>
      <TracedCustomerLayout
        router={router}
        viewer={viewer}
      >
        {children}
      </TracedCustomerLayout>
    </LayoutErrorBoundary>
  );
}

// The exported wrapper receives an opaque Relay fragment ref for viewer,
// so viewer is typed loosely here rather than as resolved data.
const customerLayoutPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

CustomerLayout.propTypes = customerLayoutPropTypes;

export default CustomerLayout;
